<script lang="ts">
  import { _ } from 'svelte-i18n';
  import {
    billableRecords,
    calculateTotalPrice,
    pricingTiers,
    maxQuotableRecords,
    quotable
  } from '$lib/pricing/pricing';
  import { applicationMode } from '$lib/stores/mode';
  import type { ApplicationMode } from '$lib/stores/mode';
  import { filteredMixedResults } from '$lib/stores/mixedResults';
  import { countState, guidedActive } from '$lib/stores/guided';
  import { queryRunning } from '$lib/stores/selection';
  import { List, User, Wallet } from 'lucide-svelte';
  import { onDestroy } from 'svelte';
  import { summaryBarHeight } from '$lib/stores/ui';
  import { previousMode } from '$lib/stores/checkout';
  import { pixel } from '$lib';
  import { env } from '$lib/config/environment';
  import ExportDialog from '$lib/components/ExportDialog.svelte';
  import type { ExportRequest } from '$lib/api/accountorder';
  import type { CompanyWithContacts } from '$lib/types/api';
  // import { track } from '$lib/analytics/tracking';

  export let selectedMode = $applicationMode;

  /** Whether the results themselves are on screen — see the button below. */
  export let canOrder = true;

  /**
   * Whether this build is the members' app. See AppMode.
   *
   * A constant rather than a store: it is what the bundle was compiled as, so
   * the bar cannot gain a price column halfway through somebody's session.
   */
  const memberApp = env.mode === 'member';

  const selectMode = (mode: ApplicationMode) => {
    if (mode !== selectedMode) {
      selectedMode = mode;
      applicationMode.set(mode);
    }
  };

  /**
   * Two sources, one bar.
   *
   * While the guided screen is up there are no results yet, so the numbers come
   * from the count route — the same exact figures the guide was showing in a
   * second bar of its own. That bar is gone: two running totals on one screen
   * is one more than the customer can act on, and they were the same numbers
   * computed the same way.
   *
   * Once a search has run, the bar describes what is on screen instead, because
   * that is what an order is built from.
   */
  // Typed now that the export reads the identifiers out of it. It was `[]`,
  // which infers any[] and hid the shape from anything that used it.
  let records: CompanyWithContacts[] = [];
  let searchedCompanies = 0;
  let searchedContacts = 0;

  $: guiding = $guidedActive && records.length === 0 && $countState.value !== null;

  $: recordCount = guiding ? ($countState.value?.companies ?? 0) : searchedCompanies;
  $: contactCount = guiding ? ($countState.value?.contacts ?? 0) : searchedContacts;

  $: billable = billableRecords(recordCount, contactCount);

  /**
   * A searched selection can still be too large to sell.
   *
   * The service trims the companies to its own cap, so `records.length` never
   * exceeds it — but a customer asking for every contact at each company buys
   * contacts, and twenty thousand companies can carry far more than twenty
   * thousand people. So the ceiling has to be tested against what is charged
   * for, which is why this reads `billable` rather than the row count.
   *
   * Without it the bar showed no price, because the price is withheld above the
   * ceiling, and offered the order button anyway — an order for an amount the
   * customer was never shown.
   */
  $: tooManyToOrder = records.length > 0 && !quotable(billable);
  $: totalPrice = calculateTotalPrice(pricingTiers, billable);

  /**
   * Whether a number on its way.
   *
   * Both sources can be in flight: the count route while the guide is being
   * filled in, and the search itself afterwards. The figures stay on screen and
   * pulse rather than being replaced by a spinner — a number that vanishes and
   * comes back reads as broken, where one that breathes reads as recalculating.
   */
  $: busy = $queryRunning || $countState.status === 'counting';

  // Plain const, not `$:`. It referenced no store, so the reactive form
  // rebuilt the same function on every unrelated update and never once
  // produced a different one.
  const swiss = (value: number) => value.toLocaleString('de-CH');

  $: stats = [
    {
      icon: List,
      label: $_('stats.companies'),
      value: recordCount > 0 ? swiss(recordCount) : '–'
    },
    {
      icon: User,
      label: $_('stats.contacts'),
      value: contactCount > 0 ? swiss(contactCount) : '–'
    },
    // The price, for the app where browsing is what is bought.
    //
    // A member pays a subscription per seat and browses within fair use; what
    // costs money is a copy leaving the app, and that is priced at export
    // rather than per row on screen. A running total beside their counts would
    // be quoting them for something they have already paid to look at. See
    // docs/refactor/exports.md.
    ...(memberApp
      ? []
      : [
          {
            icon: Wallet,
            label: $_('pricing.total'),
            // Blank above the ceiling, and the counts beside it stay. A
            // selection that large is a real answer to "how many are there";
            // it is not an answer to "what does it cost", because nothing that
            // size is delivered. See maxQuotableRecords.
            value: quotable(billable)
              ? `CHF ${totalPrice.toLocaleString('de-CH', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`
              : '–'
          }
        ])
  ];

  function onPlaceOrder() {
    // Store current mode before switching to checkout
    previousMode.set($applicationMode);

    // Track checkout initiation with Meta Pixel
    pixel.trackConversion('InitiateCheckout', {
      value: totalPrice,
      currency: 'CHF',
      content_type: 'product',
      contents: [
        {
          id: 'company_data',
          quantity: recordCount
        }
      ],
      num_items: recordCount
    });

    selectMode('checkout');
  }

  /** The export dialog's request, or null while it is closed. */
  let exporting: ExportRequest | null = null;

  /**
   * Open the export, with what is on screen.
   *
   * The rows are read here rather than in the dialog so that what is priced is
   * what the customer was looking at when they pressed the button — a filter
   * moving underneath an open dialog must not change the quote.
   *
   * Contact identifiers go along with the companies: what a customer buys is
   * the people, and a company with no deliverable contact is not a row. The
   * server resolves that and prices what it will actually deliver.
   */
  function onExport() {
    exporting = {
      company_ids: records.map((company) => company.id),
      contact_ids: records.flatMap((company) =>
        (company.contacts ?? []).map((contact) => contact.id)
      )
    };
  }

  let unsubscribe = filteredMixedResults.subscribe((data) => {
    records = data ?? [];
    searchedCompanies = records.length;
    searchedContacts = records.reduce((total, company) => {
      return total + (company.contacts?.length || 0);
    }, 0);
  });

  /**
   * The bar's own height, published for anything that has to float above it.
   *
   * `clientHeight` rather than a constant: this bar is 65px, 123px or 182px
   * depending on the width and whether the too-large notice is up. See
   * `summaryBarHeight`.
   */
  let barHeight = 0;
  $: summaryBarHeight.set(barHeight);

  onDestroy(() => {
    unsubscribe?.();
    // Nothing above it should keep reserving room for a bar that is gone.
    summaryBarHeight.set(0);
  });
</script>

<!--
  The summary bar: what the selection contains, what it costs, and the way out.

  # Why it looked glued together

  Three things, all of them alignment rather than styling. The button was the
  only rounded-xl control in a product whose controls are rounded-control, the
  only one carrying a shadow, and the only one painted by a local gradient
  written past the shared button styles. And it was 34px tall standing beside
  40px of stacked text, so it sat visibly off the row's centre line.

  The stats had their own version of the same problem: an icon vertically
  centred against a two-line stack floats between the two lines, aligning to
  neither. They are label-over-value stacks now, which share a left edge and a
  baseline, and the icons are gone — with "Firmen", "Kontakte" and "Total"
  written out, they were decoration that cost the alignment.

  The row has a fixed height and everything is centred in it, so the button
  belongs to the band rather than being stuck onto the end of it.

  # Why the bar is the height of the top bar

  Because the shell is three rows and two of them are chrome: the same 64px
  band top and bottom frames the working area evenly, which is a decision made
  once rather than a number tuned per bar.

  It was 80px for a while, to put this button on the same centre line as the
  sidebar's search button — the two sat side by side when the sidebar ran the
  full height of the window, so they had to be matched by arithmetic. The rows
  removed that problem rather than solving it: the sidebar now ends where this
  bar begins, and the search button sits above it.

  # The three columns

  A summary band reads as composed when its content sits in the middle of it,
  not when the content is pushed to one edge and the action to the other. So
  the row is a grid of three: an empty column, the stats centred in the bar's
  full width, and the button at the end. The empty column is what does the
  work — without it the stats would centre in whatever space the button leaves,
  which moves them off-centre by half the button's width and changes as the
  label translates.

  Only from `lg`, though. The stats column is sized to its content and the two
  outer columns split what is left, so between `sm` and `lg` the button's
  column came out narrower than the button — and it spilled left over the
  price, in the public app where there are three figures. Below `lg` the row is
  figures-then-button instead, which always has room for both.
-->
<!--
  Tinted, not solid navy — and the difference is the rule.

  Solid navy is right for the top bar because that bar carries no action: a
  dark band recedes, which is what a frame should do. This bar exists almost
  entirely to carry the export button, and a control whose whole job is to
  advance does not want the most recessive surface in the window under it. The
  two fight, and what you see is a saturated button stranded on a dark strip.

  So the chrome splits in two by what it holds. Chrome with no action in it is
  solid navy. Chrome that carries one is the same navy at eight percent — the
  identical wash the card headers use, so it still reads as frame against the
  white content, while the button sits on a near-white ground where a
  saturated accent works the way it was drawn to.

  The hairline stays here for the same reason it went at the top: eight
  percent navy and white *are* nearly the same colour, so they need the line.
-->
<div
  bind:clientHeight={barHeight}
  class="z-30 w-full flex-shrink-0 border-t border-hairline bg-surface-chrome"
>
  <!--
    Why the selection cannot be bought, in the bar rather than on the button.

    It was a `title` on the disabled button, which is a hover affordance: a
    customer on a touch screen, or moving by keyboard, met a dead control with
    no reason given. The bar is where the numbers this contradicts are already
    being read, so the explanation belongs beside them.

    Above the row rather than inside it, so the 64px band that frames the
    working area keeps its height. The strip only exists when it applies, which
    is the exception — a selection this large is one somebody has to go out of
    their way to build.

    The amber panel is the guided screen's own too-large notice, which says the
    same kind of thing about the same number. See GuidedSearch.
  -->
  {#if tooManyToOrder}
    <div class="border-b border-amber-200 bg-amber-50 px-4 py-2.5 sm:px-6 lg:px-8">
      <p class="text-sm text-amber-900">
        {$_('pricing.tooManyToOrder.message', {
          values: { count: swiss(billable), cap: swiss(maxQuotableRecords) }
        })}
      </p>
      <p class="mt-0.5 text-xs text-amber-800/80">{$_('pricing.tooManyToOrder.hint')}</p>
    </div>
  {/if}

  <div
    class="mx-auto flex w-full flex-col gap-2 px-4 py-2 sm:grid sm:h-16 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-6 sm:px-6 sm:py-0 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:px-8"
  >
    <div class="hidden lg:block" aria-hidden="true"></div>

    <!--
      Spaced generously, because three figures crowded together read as one
      block of numbers rather than three separate answers.

      # The icon sits beside the number, not beside the pair

      The icons were dropped for a while because they broke the alignment: an
      icon vertically centred against a two-line stack floats between the two
      lines and aligns to neither. Here the icon shares a row with the value —
      two single-line boxes, so `items-center` lines them up exactly — and the
      label sits under both. The icon can come back because it is measured
      against something its own height.
    -->
    <!--
      Smaller below `sm`, where this bar stops being a 64px strip.

      From `sm` up it is one row and the figures have the height to themselves.
      Below it the bar stacks them over the export button, so 20px numerals and
      a 20px icon were setting the height of the whole thing — measured, 123px
      of a phone's screen, against 65px on a desktop. `text-base` and a 16px
      icon give the same three figures back most of that without making them
      hard to read, and the surrounding padding comes in with them.

      Two columns in the members' app, three in the public one: the grid
      follows the figures rather than reserving a slot for a price that is not
      shown. Below `sm` it is a plain row spread edge to edge, because equal
      columns on a phone are narrower than "CHF 5'713.10" and broke it in two.

      The button is shorter on a phone for the same reason: a 40px full-width
      slab under three small figures was the heaviest thing on the screen.
    -->
    <div
      class="flex items-center justify-between sm:grid sm:justify-normal sm:gap-6 md:gap-14 lg:gap-20 xl:gap-28 {memberApp
        ? 'sm:grid-cols-[repeat(2,max-content)]'
        : 'sm:grid-cols-[repeat(3,max-content)]'}"
    >
      {#each stats as stat (stat.label)}
        <div class="flex flex-col items-start gap-0.5 sm:items-center sm:justify-self-center">
          <div class="flex items-center gap-2" class:animate-pulse={busy}>
            <svelte:component
              this={stat.icon}
              class="h-4 w-4 shrink-0 text-ink-400 sm:h-5 sm:w-5"
            />
            <!--
              tabular-nums, because these change while the customer watches.
              With proportional figures the whole bar shifts sideways every
              time a filter moves a count, which reads as instability.
            -->
            <span
              class="whitespace-nowrap text-base font-semibold tabular-nums text-ink-800 sm:text-lg md:text-xl"
            >
              {stat.value}
            </span>
          </div>
          <span class="text-2xs uppercase tracking-wide text-ink-500">{stat.label}</span>
        </div>
      {/each}
    </div>

    <!--
      The button belongs to the data view.

      The bar itself does not: while the guide is being filled in it carries the
      running count, which is the whole reason the guide has no counters of its
      own. But ordering is an action on a selection that exists, and on those
      screens it does not yet — so the offer to buy it stood there through every
      step of a sequence whose last step is what produces the thing to buy.

      The column stays, empty. It is what keeps the figures in the middle of the
      bar rather than in the middle of whatever space the button leaves.
    -->
    {#if canOrder}
      <!--
        Two different acts, and they read as two different words.

        A visitor places an order: they are buying the data, and the flow
        collects a name, an address and payment. A member exports: they have
        already paid to look at it, and what costs money is the copy leaving.
        Same button, same place, different journey — so the label says which.
      -->
      <button
        class="inline-flex h-8 w-full items-center justify-center whitespace-nowrap rounded-control bg-accent-600 px-4 text-sm sm:h-10 sm:px-5 font-semibold text-white transition-colors hover:bg-accent-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 sm:w-auto sm:justify-self-end"
        on:click={memberApp ? onExport : onPlaceOrder}
        disabled={records.length === 0 || tooManyToOrder}
      >
        {memberApp ? $_('export.action') : $_('buttons.placeOrder')}
      </button>
    {:else}
      <div class="hidden lg:block" aria-hidden="true"></div>
    {/if}
  </div>
</div>

{#if exporting}
  <ExportDialog request={exporting} onClose={() => (exporting = null)} />
{/if}
